import React, { useState, useEffect } from 'react';
import { Star, Quote, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../data/mockData';
import { fetchTestimonials } from '../utils/api';
import { toast } from 'sonner';

const Testimonials = () => {
  const { language } = useLanguage();
  const t = translations[language].testimonials;
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      const data = await fetchTestimonials();
      setTestimonials(data);
    } catch (error) {
      console.error('Error loading testimonials:', error);
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section id="testimonials" className="py-24 bg-gradient-to-b from-[#0a0c0f] to-[#141619] circuit-pattern">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 text-center">
          <div className="text-[#D4AF37] text-xl">Loading testimonials...</div>
        </div>
      </section>
    );
  }

  return (
    <section id="testimonials" className="py-24 bg-gradient-to-b from-[#0a0c0f] to-[#141619] circuit-pattern relative">
      {/* Decorative line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent"></div>
      
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 mb-6 px-4 py-2 bg-gradient-to-r from-[#1a1d23] to-[#141619] rounded-full border border-[#D4AF37]/30">
            <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs font-mono uppercase tracking-widest text-[#D4AF37] font-semibold">
              {language === 'en' ? 'Client Success' : language === 'es' ? 'Éxito del Cliente' : 'Kundenerfolg'}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            <span className="text-gold-metallic">{t.title.split(' ').slice(0, 2).join(' ')}</span>
            <br />
            {t.title.split(' ').slice(2).join(' ')}
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="group p-8 bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-2xl border border-[#2a3441] hover:border-[#D4AF37]/50 transition-all duration-300 hover:scale-[1.02] relative overflow-hidden"
            >
              {/* Hover glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/0 to-[#D4AF37]/0 group-hover:from-[#D4AF37]/5 group-hover:to-transparent transition-all duration-300"></div>
              
              {/* Quote icon */}
              <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity duration-300">
                <Quote className="w-16 h-16 text-[#D4AF37]" />
              </div>

              <div className="relative z-10">
                {/* Rating */}
                <div className="flex space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 fill-[#D4AF37] text-[#D4AF37]"
                    />
                  ))}
                </div>

                {/* Testimonial text */}
                <p className="text-base text-gray-300 leading-relaxed mb-6">
                  "{testimonial.text[language]}"
                </p>

                {/* Author with premium styling */}
                <div className="pt-6 border-t border-[#2a3441]">
                  <div className="flex items-center space-x-3">
                    {/* Avatar placeholder with gradient */}
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4C542] flex items-center justify-center text-[#141619] font-bold text-lg">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-bold text-white group-hover:text-[#D4AF37] transition-colors">
                        {testimonial.name}
                      </div>
                      <div className="text-sm text-gray-400 flex items-center space-x-1">
                        <span>{testimonial.company}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Corner accent */}
              <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-[#5A6F8F]/20 to-transparent rounded-tr-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          ))}
        </div>

        {/* Trust indicators */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: '5.0', label: language === 'en' ? 'Average Rating' : language === 'es' ? 'Calificación Promedio' : 'Durchschnittliche Bewertung' },
            { value: '100%', label: language === 'en' ? 'Client Satisfaction' : language === 'es' ? 'Satisfacción del Cliente' : 'Kundenzufriedenheit' },
            { value: '50+', label: language === 'en' ? 'Happy Clients' : language === 'es' ? 'Clientes Felices' : 'Zufriedene Kunden' },
            { value: '24/7', label: language === 'en' ? 'Support' : language === 'es' ? 'Soporte' : 'Support' },
          ].map((stat, index) => (
            <div
              key={index}
              className="text-center p-6 bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-xl border border-[#2a3441]"
            >
              <div className="text-2xl font-bold text-[#D4AF37] mb-2">{stat.value}</div>
              <div className="text-xs text-gray-400 uppercase tracking-wide">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Bottom decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#5A6F8F]/30 to-transparent"></div>
    </section>
  );
};

export default Testimonials;
